import React, { useState, useContext, useEffect, Fragment } from 'react';
import {
  Button,
  Dialog,
  makeStyles,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip
} from '@material-ui/core';
import LimitedBackdrop from '../../../components/UI/LimitedBackdrop';
import CustomDatePicker from '../../../components/UI/CustomDatePicker';
import DropZone from '../../../components/UI/DropZone';
import { separations } from '../../../constants/StyleConstants';
import AreaSelect from '../../../components/AreaSelect';
import notificationsAPI from '../../../services/NotificationsApi';
import { AppContext } from '../../../contexts/AppContext';
import { InventoriesContext } from '../../../contexts/InventoriesContext';
import {
  SupplierMultiSelect,
  CriticalSuppliersMultiSelect,
  RegionMultiSelect,
  CountryMultiSelect,
  SendToRadioButtons,
  AttachmentContainer,
  TreasuryGroupsMultiSelect,
} from './NotificationDetailsModalComponents';
import { LoginContext } from '../../../contexts/LoginContext';
import { NOTIFICATION } from '../../../constants/GlobalConstants';
import GroupedChips from '../../../components/UI/GroupedChips';
import VideoPlayer from '../../../components/VideoPlayer';
import { AddCircle, RemoveCircle } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  formControl: {
    minWidth: 120,
  },
  rigthSeparation: {
    marginRight: 10,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  middleAlignmentExpanded: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  middleAlignment: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  supplierPicker: {
    display: 'flex',
    flexDirection: 'column',
  },
  chip: {
    margin: 2,
  },
  chipsWrapper: {
    marginTop: 10,
  },
  backdrop: {
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  radioButtonContainer: {
    display: 'flex',
    columnGap: '1em',
    '& div[role="radiogroup"]': {
      flexDirection: 'row !important',
    },
  },
  addReceiverBtn: {
    padding: 0,
    margin: '0 .5em .1em .5em',
  },
  ...separations,
}));

const Receivers = ({ groupType, receivers, setReceivers, readOnly }) => {
  const classes = useStyles();

  switch (groupType) {
    case NOTIFICATION.SUPPLIER.GROUP_TYPE:
      return <SupplierMultiSelect classes={classes} selectedItems={receivers} setSelectedItems={setReceivers} readOnly={readOnly} />;
    case NOTIFICATION.REGION.GROUP_TYPE:
      return <RegionMultiSelect classes={classes} selectedItems={receivers} setSelectedItems={setReceivers} readOnly={readOnly} />;
    case NOTIFICATION.COUNTRY.GROUP_TYPE:
      return <CountryMultiSelect classes={classes} selectedItems={receivers} setSelectedItems={setReceivers} readOnly={readOnly} />;
    case NOTIFICATION.TREASURY_GROUPS.GROUP_TYPE:
      return <TreasuryGroupsMultiSelect classes={classes} selectedItems={receivers} setSelectedItems={setReceivers} readOnly={readOnly} />;
    case NOTIFICATION.CRITICAL_SUPPLIER.GROUP_TYPE:
      return <CriticalSuppliersMultiSelect classes={classes} selectedItems={receivers} setSelectedItems={setReceivers} readOnly={readOnly} />;
    default:
      return <></>;
  }
};

const NotificationDetailsModal = ({ open, setOpen, reloadGrid, notificationId }) => {
  const classes = useStyles();
  const { isSupplier, isAdmin, userInfo, idSupplier } = useContext(LoginContext);
  const { getTheRegions, getTheCountries, getTheSuppliers, getTheTreasuryGroups, getTheCriticalSuppliers } = useContext(InventoriesContext);
  const [area, setArea] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dropZoneTransIn, setDropZoneTransIn] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [mail, setMail] = useState(false);
  const [popUp, setPopUp] = useState(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [groupType, setGroupType] = useState(NOTIFICATION.SUPPLIER.GROUP_TYPE);
  const [receivers, setReceivers] = useState([]);
  const [result, setResult] = useState('');
  const [vencimientoDate, setVencimientoDate] = useState('');
  const [conformidad, setConformidad] = useState();
  const [video, setVideo] = useState('');

  const { showError, showWarning, showSuccess } = useContext(AppContext);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const[status, setStatus] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoSaved, setVideoSaved] = useState('');

  const setReceiversById = (groupType, receiversIds) => {
    switch (groupType) {
      case NOTIFICATION.ALL_SUPPLIER.GROUP_TYPE:
      case NOTIFICATION.SUPPLIER.GROUP_TYPE: {
        !isSupplier && getTheSuppliers().then(res => setReceivers(res.filter(r => receiversIds.includes(r.cuit))));
        break;
      }
      case NOTIFICATION.REGION.GROUP_TYPE:
        getTheRegions().then(res => setReceivers(res.filter(r => receiversIds.includes(r.id))));
        break;
      case NOTIFICATION.COUNTRY.GROUP_TYPE:
        getTheCountries().then(res => setReceivers(res.filter(r => receiversIds.includes(r.id))));
        break;
      case NOTIFICATION.TREASURY_GROUPS.GROUP_TYPE:
        getTheTreasuryGroups().then(res => setReceivers(res.filter(r => receiversIds.includes(r.id))));
        break;
      case NOTIFICATION.CRITICAL_SUPPLIER.GROUP_TYPE:
        !isSupplier && getTheCriticalSuppliers().then(res => setReceivers(res.filter(r => receiversIds.includes(r.cuit))));
        break;
      default:
        showError('No se puede extraer destinatarios');
    }
  };

  const getReceiversIds = () => {
    switch (groupType) {
      case NOTIFICATION.SUPPLIER.GROUP_TYPE:
      case NOTIFICATION.CRITICAL_SUPPLIER.GROUP_TYPE:
        return receivers.map(e => e.cuit);
      case NOTIFICATION.REGION.GROUP_TYPE:
      case NOTIFICATION.COUNTRY.GROUP_TYPE:
      case NOTIFICATION.TREASURY_GROUPS.GROUP_TYPE:
        return receivers.map(e => e.id);
      case NOTIFICATION.ALL_SUPPLIER.GROUP_TYPE:
        return [];
      default:
        showError('No se puede extraer destinatarios');
    }
  };

  const resetFields = () => {
    setArea('');
    setTitle('');
    setContent('');
    setSelectedDate(new Date());
    setAttachedFiles([]);
    setMail(false);
    setPopUp(false);
    setGroupType(NOTIFICATION.SUPPLIER.GROUP_TYPE);
    setReceivers([]);
    setResult('');
    setVencimientoDate(null);
    setConformidad(false);
    setVideo('');
    setIsConfirmed(false);
    setShowVideo(false);
  };

  const handleFiles = files => {
    setAttachedFiles([...attachedFiles, ...files]);
    setDropZoneTransIn(false);
  };

  const handleChangeGroupType = e => {
    setGroupType(e.target.value);
    if (e.target.value == NOTIFICATION.CRITICAL_SUPPLIER.GROUP_TYPE)
      getTheCriticalSuppliers().then(res => setReceivers(res));
    else
      setReceivers([]);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const finish = () => {
    setOpen(false);
    setTimeout(() => {
      resetFields();
      setOpenBackDrop(false);
    }, 300);
  };

  const handleSave = () => {
    setOpenBackDrop(true);

    const request = {
      IdArea: area,
      Title: title,
      Content: content,
      Email: mail,
      Popup: popUp,
      Files: attachedFiles.length,
      IdGroupType: groupType,
      Receivers: getReceiversIds(groupType),
      Result: result,
      FechaHasta: vencimientoDate,
      Enlace: videoSaved,
      ConformidadRequerida: conformidad,
    };

    notificationsAPI
      .newNotification(request)
      .then(result => {
        if (!attachedFiles.isEmpty()) {
          notificationsAPI
            .uploadFile(result.id, attachedFiles)
            .then(res => {
              reloadGrid();
              showSuccess('Notificación creada con éxito, ver resultados del envío en la Notificación');
              finish();
            })
            .catch(error => {
              finish();
              showError('No se pudieron adjuntar los archivos a la notificación');
            });
        } else {
          reloadGrid();
          showSuccess('Notificación creada con éxito, ver resultados del envío en la Notificación');
          finish();
        }
      })
      .catch(error => {
        finish();
        showError('No se pudo crear la notificacion');
      });
  };

  const validate = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = !area)) {
      errorMessages.push('Debe seleccionar un área para la notificación');
    }

    if ((errors[i++] = !title)) {
      errorMessages.push('Debe completar el título de la notificación');
    }

    if ((errors[i++] = !content)) {
      errorMessages.push('Debe completar el contenido de la notificación');
    }

    if ((errors[i++] = !mail && !popUp)) {
      errorMessages.push('Debe seleccionar al menos un tipo de notificación');
    }

    if (groupType != NOTIFICATION.ALL_SUPPLIER.GROUP_TYPE)
      if ((errors[i++] = receivers.isEmpty())) {
        errorMessages.push('Debe seleccionar al menos un destinatario');
      }

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const validateAndSave = () => validate() && handleSave();

  const handleConfirm = (idSupplier, idNotification) => {
    console.log("entra handleConfirm")
    notificationsAPI.confirmNotification(idSupplier, idNotification)
    .then( res => showSuccess('Adhesión confirmada'))
    .catch( error => showError('No se pudo cofirmar la adhesión'))
    .finally(finish())
  }


  useEffect(() => {
    if (open && notificationId) {
      setOpenBackDrop(true);
      notificationsAPI.getNotificationDetail(notificationId).then(res => {
        setArea(res.idArea);
        setTitle(res.title);
        setContent(res.content);
        setSelectedDate(res.date);
        setMail(res.email);
        setPopUp(res.popup);
        setGroupType(res.groupType.toString());
        setReceiversById(res.groupType.toString(), res.receivers);
        setAttachedFiles(res.files);
        setOpenBackDrop(false);
        setResult(res.result);
        setConformidad(res.conformidadRequerida);
        setVideo(res.enlace);
        setStatus(res.status);

        if (res.fechaHasta != null) {
          setVencimientoDate(res.fechaHasta);
        }
        else {
          setVencimientoDate(null);
        }
      });
    } else if (open && !notificationId && !isAdmin) {
      setArea(userInfo.idArea);
    } else {
      setTimeout(() => {
        resetFields();
      }, 300);
    }
  }, [open]);

  const handleToggle = (url) => {
    if (showVideo) {
      setShowVideo(false);
    } else {
      const extractedSrc = extractVideoSrc(url);
      if (extractedSrc) {
        
        setShowVideo(true);
      } else {
        alert('No se pudo extraer la previsualizacion del video.');
      }
    }
  };

  const extractVideoSrc = (input) => {  if (!input) return null;
    // Para cuando contiene un iframe con src  
    const iframeMatch = input.match(/<iframe[^>]*src=["']([^"']+)["']/);
     if (iframeMatch) return iframeMatch[1];
     // Para cuando contiene solo la URL dentro del src=  
      const srcMatch = input.match(/src=["']([^"']+)["']/);
      if (srcMatch) return srcMatch[1];
     // Para cuando es una URL limpia (ya usable)  
     if (input.startsWith('https://') || input.startsWith('http://')) return input;
    // Si no matchea nada, null  
    return null;
  };

  return (
    <Dialog disableBackdropClick={!notificationId} onClose={finish} scroll="body" aria-labelledby="customized-dialog-title" open={open}>
      <LimitedBackdrop open={openBackDrop}>
        <CircularProgress color="primary" />
      </LimitedBackdrop>
      <DialogTitle id="customized-dialog-title">{notificationId ? 'Ver Notificación' : 'Nueva Notificación'}</DialogTitle>
      <DialogContent dividers>
        { notificationId ?
          (
            <div>
              <AreaSelect disableOptionAll readOnly={!isAdmin || notificationId} style={{ marginBottom: 8 }} value={area} setValue={setArea} />
          <TextField
            fullWidth
            margin="dense"
            id="title"
            inputProps={{ readOnly: !!notificationId, maxLength: 25 }}
            label="Título"
            onChange={e => setTitle(e.target.value)}
            value={title}
          />
          {
            isAdmin && (
              <TextField
              className={classes.topSeparation}
              fullWidth
              margin="dense"
              id="outlined-multiline-static"
              multiline
              inputProps={{ readOnly: !!notificationId }}
              rows={4}
              variant="outlined"
              label="Contenido"
                onChange={e => setContent(e.target.value)}
                value={content}
              />
            )
          }
          {
            video && (
              <>
                <VideoPlayer src={ video }/>

                {
                  isSupplier &&  conformidad && (
                    <TextField
                      className={classes.topSeparation}
                      fullWidth
                      margin="dense"
                      id="outlined-multiline-static"
                      multiline
                      inputProps={{ readOnly: !!notificationId }}
                      rows={4}
                      variant="outlined"
                      label="Contenido"
                      onChange={e => setContent(e.target.value)}
                      value={content}
                    />
                  )
                }
                
              </>
              
              
            )
          }
          
          {
            isSupplier && conformidad && (
              <FormControlLabel
              control={
                <Checkbox
                  disabled={status == 3}
                  color="primary"
                  checked={ status != 3 ? isConfirmed : true}
                  onChange={e => setIsConfirmed(e.target.checked)}
                  name="Confirmacion"
                />
              }
              label= {status != 3 ? "Confirmo adhesión" : "Adhesión confirmada"}
            />
            )
          }
          

          <div className={classes.middleAlignmentExpanded}>
            <CustomDatePicker
              readOnly
              value={selectedDate}
              disablePast={!notificationId}
              setValue={setSelectedDate}
              onChange={setSelectedDate}
              margin="dense"
            />
            {vencimientoDate &&
              <CustomDatePicker
                readOnly={vencimientoDate}
                label="Hasta"
                value={vencimientoDate}
                //disablePast={vencimientoDate}
                setValue={setVencimientoDate}
                onChange={setVencimientoDate}
                margin="dense"
                //minDate={vencimientoDate ? new Date() : null}
              />}
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!!notificationId}
                  color="primary"
                  checked={mail}
                  onChange={e => setMail(e.target.checked)}
                  name="mail"
                />
              }
              label="Mail"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!!notificationId}
                  color="primary"
                  checked={popUp}
                  onChange={e => setPopUp(e.target.checked)}
                  name="Pop Up"
                />
              }
              label="Pop Up"
            />

          </div>
          <div className={classes.middleAlignmentExpanded}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!!notificationId}
                  color="primary"
                  checked={conformidad}
                  onChange={e => setConformidad(e.target.checked)}
                  name="conformidad"
                />
              }
              label="Requiere conformidad"
            />
            
            
          </div>
          {!isSupplier && (
            <Fragment>
              <SendToRadioButtons readOnly={!!notificationId} classes={classes} groupType={groupType} onChange={handleChangeGroupType} />
              <Receivers groupType={groupType} receivers={receivers} setReceivers={setReceivers} readOnly={!!notificationId} />
            </Fragment>
          )}
          {notificationId ? (
            <GroupedChips label="Archivos adjuntos" options={attachedFiles} />
          ) : (
            <AttachmentContainer
              classes={classes}
              attachedFiles={attachedFiles}
              setAttachedFiles={setAttachedFiles}
              setDropZoneTransIn={setDropZoneTransIn}
            />
          )}
          {!isSupplier && notificationId && (
            <TextField
              className={classes.topSeparation}
              fullWidth
              margin="dense"
              id="outlined-multiline-static"
              multiline
              inputProps={{ readOnly: !!notificationId }}
              rows={5}
              variant="outlined"
              label="Resultado"
              onChange={e => setResult(e.target.value)}
              value={result}
            />
          )}
            </div>
          
        
          ) : (
            <DropZone open={dropZoneTransIn} setOpen={setDropZoneTransIn} handleFiles={handleFiles} acceptedFiles={['application/pdf']}>
          <AreaSelect disableOptionAll readOnly={!isAdmin || notificationId} style={{ marginBottom: 8 }} value={area} setValue={setArea} />
          <TextField
            fullWidth
            margin="dense"
            id="title"
            inputProps={{ readOnly: !!notificationId, maxLength: 25 }}
            label="Título"
            onChange={e => setTitle(e.target.value)}
            value={title}
          />
          <TextField
            className={classes.topSeparation}
            fullWidth
            margin="dense"
            id="outlined-multiline-static"
            multiline
            inputProps={{ readOnly: !!notificationId }}
            rows={4}
            variant="outlined"
            label="Contenido"
            onChange={e => setContent(e.target.value)}
            value={content}
          />
          <div className={classes.middleAlignment}>
          <TextField
            fullWidth
            margin="dense"
            id="video"
            inputProps={{ readOnly: !!notificationId }}
            label="Video adjunto"
            onChange={e => setVideo(e.target.value)}
            value={video}
            readOnly={video}
            disabled={showVideo}
            helperText={
              video.trim() == '' ? '' :
              !extractVideoSrc(video) ? 'No de puede cargar el video' : ''
            }
          />
          
            <IconButton className={classes.addReceiverBtn}  color="primary" aria-label="addReceiver" onClick={() => handleToggle(video)} disabled={!extractVideoSrc(video)} >
              {
                showVideo ? <RemoveCircle color='secondary' fontSize="large" onClick={() => {setVideo(''); setVideoSaved('')}}/>:
                <AddCircle fontSize="large" onClick={() => {setVideoSaved(video); setShowVideo(true)}}/>
              }
            </IconButton>
          
          
          </div>
           
          {
            showVideo && 
            <div >
              <VideoPlayer src={ extractVideoSrc(video)}/>
            </div>
            
          }
          
          
          <div className={classes.middleAlignmentExpanded}>
            <CustomDatePicker
              readOnly
              value={selectedDate}
              disablePast={!notificationId}
              setValue={setSelectedDate}
              onChange={setSelectedDate}
              margin="dense"
            />
            <CustomDatePicker
              readOnly={!!notificationId}
              label="Hasta"
              value={vencimientoDate}
              disablePast={!notificationId}
              setValue={setVencimientoDate}
              onChange={setVencimientoDate}
              margin="dense"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!!notificationId}
                  color="primary"
                  checked={mail}
                  onChange={e => setMail(e.target.checked)}
                  name="mail"
                />
              }
              label="Mail"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!!notificationId}
                  color="primary"
                  checked={popUp}
                  onChange={e => setPopUp(e.target.checked)}
                  name="Pop Up"
                />
              }
              label="Pop Up"
            />
          </div>
          <div className={classes.middleAlignmentExpanded}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!!notificationId}
                  color="primary"
                  checked={conformidad}
                  onChange={e => setConformidad(e.target.checked)}
                  name="conformidad"
                />
              }
              label="Requiere conformidad"
            />
          </div>
          {!isSupplier && (
            <Fragment>
              <SendToRadioButtons readOnly={!!notificationId} classes={classes} groupType={groupType} onChange={handleChangeGroupType} />
              <Receivers groupType={groupType} receivers={receivers} setReceivers={setReceivers} readOnly={!!notificationId} />
            </Fragment>
          )}
          {notificationId ? (
            <GroupedChips label="Archivos adjuntos" options={attachedFiles} />
          ) : (
            <AttachmentContainer
              classes={classes}
              attachedFiles={attachedFiles}
              setAttachedFiles={setAttachedFiles}
              setDropZoneTransIn={setDropZoneTransIn}
            />
          )}
          {!isSupplier && notificationId && (
            <TextField
              className={classes.topSeparation}
              fullWidth
              margin="dense"
              id="outlined-multiline-static"
              multiline
              inputProps={{ readOnly: !!notificationId }}
              rows={5}
              variant="outlined"
              label="Resultado"
              onChange={e => setResult(e.target.value)}
              value={result}
            />
          )}
        </DropZone>
          )
        } 
      </DialogContent>
      <DialogActions>
        {!notificationId && (
          <Button autoFocus onClick={validateAndSave} color="primary">
            Guardar
          </Button>
        )}
        {notificationId && isSupplier && conformidad && status != 3 && (
          <Button autoFocus onClick={() => handleConfirm(userInfo.id, notificationId)} color="primary" disabled={!isConfirmed}>
            Aceptar
          </Button>
        )}
        <Button autoFocus onClick={handleClose} color="secondary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationDetailsModal;
